/* eslint-disable no-restricted-syntax */
/**
 * UnifiedLinkingView - Main container for the unified entity linking UI
 *
 * Features:
 * - Two-column layout with Finch entities on left, Filevine on right
 * - SVG overlay for animated bezier curve connections
 * - Drag-to-link functionality for manual link creation
 * - Smart sorting: linked entities align horizontally, unlinked at bottom
 */

import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, CircleDashed, Sparkles, Users } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';

import { cn } from '@/lib/utils';
import type { FinchEntity } from '@/services/api/entitySyncService';
import { useEntitySyncListDataSuggestionsQuery } from '@/services/api/entitySyncService';
import type {
  DetailedFilevineCollectionItemSchema,
  FilevineEntitySyncSuggestionSetSchema,
} from '@/services/api/generated/entitySyncService';

import { usePolling, usePollingWhile } from '../context';
import { useLinkingState } from '../hooks/useLinkingState';
import { SuggestionSetDetailsSheet } from '../sync-suggestions/SuggestionSetDetailsSheet';
import { useSyncSuggestionsMap } from '../sync-suggestions/useSyncSuggestionsMap';
import type {
  ConnectionLineData,
  DragLinkResult,
  Point,
  UnifiedLinkingViewProps,
} from '../types/linking-view.types';
import { ConnectionCanvas } from './ConnectionCanvas';
import { LinkingColumn } from './LinkingColumn';
import { useDragToLink } from './useDragToLink';
import { useEntityPositions } from './useEntityPositions';

/**
 * Calculate bezier control points for smooth curves
 */
function calculateConnectionLine(
  startPoint: Point | null,
  endPoint: Point | null,
  linkId: string,
  status: 'approved' | 'suggested',
  confidence: number,
): ConnectionLineData | null {
  if (!startPoint || !endPoint) return null;

  return {
    id: `line-${linkId}`,
    start: startPoint,
    end: endPoint,
    status,
    confidence,
    linkId,
  };
}

export const UnifiedLinkingView: React.FC<UnifiedLinkingViewProps> = ({
  matterId,
  firmId,
  canonicalSectionType,
  fieldConfigs,
  sectionConfigData,
  finchEntities,
  filevineEntities,
  suggestedLinks,
  approvedLinks,
  onApprove,
  onReject,
  onCreateLink,
  isGeneratingSuggestions,
  onGenerateSuggestions,
  isGeneratingSyncSuggestions,
  onGenerateSyncSuggestions,
  hideHeader = false,
  className,
}) => {
  // Enable polling while generating sync suggestions
  usePollingWhile(isGeneratingSyncSuggestions ?? false);

  // Get polling state from context
  const { pollingInterval } = usePolling();

  // Compute sorted entities and resolved links
  const { state, getLinkForEntity } = useLinkingState({
    finchEntities,
    filevineEntities,
    suggestedLinks,
    approvedLinks,
  });

  // Fetch sync suggestions - uses polling interval from context
  const { data: syncSuggestionsData } = useEntitySyncListDataSuggestionsQuery(
    {
      matterId,
      entityType: canonicalSectionType,
    },
    {
      pollingInterval,
      skipPollingIfUnfocused: true,
    },
  );
  const suggestionSets = syncSuggestionsData?.suggestion_sets;
  const syncSuggestionsMap = useSyncSuggestionsMap(suggestionSets);

  // Sync suggestion details sheet state
  const [selectedSyncSuggestions, setSelectedSyncSuggestions] = useState<
    FilevineEntitySyncSuggestionSetSchema[] | null
  >(null);
  const [isSyncSheetOpen, setIsSyncSheetOpen] = useState(false);

  // Handle sync suggestion badge click
  const handleSyncSuggestionClick = useCallback(
    (suggestions: FilevineEntitySyncSuggestionSetSchema[]) => {
      setSelectedSyncSuggestions(suggestions);
      setIsSyncSheetOpen(true);
    },
    [],
  );

  // Handle sync sheet close
  const handleSyncSheetOpenChange = useCallback((open: boolean) => {
    setIsSyncSheetOpen(open);
    if (!open) {
      setTimeout(() => setSelectedSyncSuggestions(null), 300);
    }
  }, []);

  // Look up entities for the selected sync suggestion
  const selectedSyncEntities = useMemo(() => {
    if (!selectedSyncSuggestions || selectedSyncSuggestions.length === 0) {
      return { finchEntity: null, filevineEntity: null };
    }

    const suggestionSet = selectedSyncSuggestions[0];

    // Find Finch entity by finch_entity_id
    let finchEntity: FinchEntity | null = null;
    if (suggestionSet.finch_entity_id) {
      const found = finchEntities.find(
        (e) => e.id === suggestionSet.finch_entity_id,
      );
      if (found) {
        finchEntity = found;
      }
    }

    // Find Filevine entity by filevine_collection_item_id
    let filevineEntity: DetailedFilevineCollectionItemSchema | null = null;
    if (suggestionSet.filevine_collection_item_id) {
      const found = filevineEntities.find(
        (e) => e.id === suggestionSet.filevine_collection_item_id,
      );
      if (found) {
        filevineEntity = found;
      }
    }

    return { finchEntity, filevineEntity };
  }, [selectedSyncSuggestions, finchEntities, filevineEntities]);

  // Track entity positions for connection lines
  const { containerRef, registerConnectionDot, getConnectionPoint } =
    useEntityPositions();

  // Drag-to-link state
  const {
    dragState,
    startDrag,
    updateDragPosition,
    endDrag,
    cancelDrag,
    setValidDropTarget,
  } = useDragToLink({
    finchEntities: state.sortedFinchEntities.map((s) => s.entity),
    filevineEntities: state.sortedFilevineEntities.map((s) => s.entity),
  });

  // Handle drag start from entity card
  const handleDragStart = useCallback(
    (entityId: string, entityType: 'finch' | 'filevine', position: Point) => {
      startDrag(entityId, entityType, position);
    },
    [startDrag],
  );

  // Handle drag end and create link
  const handleDragEnd = useCallback(async () => {
    const result: DragLinkResult | null = endDrag();
    if (result) {
      await onCreateLink(result.finchEntityId, result.filevineEntityId);
    }
  }, [endDrag, onCreateLink]);

  // Handle mouse move during drag
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (dragState.isDragging && dragState.source) {
        // Find the linking container for proper coordinate calculation
        const linkingContainer = containerRef.current?.querySelector(
          '[data-linking-container]',
        ) as HTMLElement | null;

        if (linkingContainer) {
          const rect = linkingContainer.getBoundingClientRect();
          updateDragPosition({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
          });

          // Detect drop target using elementsFromPoint
          const elementsAtPoint = document.elementsFromPoint(
            e.clientX,
            e.clientY,
          );

          // Find the entity card wrapper under the cursor
          let foundDropTarget: string | null = null;
          for (const el of elementsAtPoint) {
            const entityEl = (el as HTMLElement).closest('[data-entity-id]');
            if (entityEl) {
              const entityId = entityEl.getAttribute('data-entity-id');
              if (entityId && entityId !== dragState.source.entityId) {
                // Check if this is in the opposite column
                const isFinchEntity = state.sortedFinchEntities.some(
                  (s) => s.entity.id === entityId,
                );
                const isFilevineEntity = state.sortedFilevineEntities.some(
                  (s) => s.entity.id === entityId,
                );

                // Only valid if it's in the opposite column
                if (
                  (dragState.source.entityType === 'finch' &&
                    isFilevineEntity) ||
                  (dragState.source.entityType === 'filevine' && isFinchEntity)
                ) {
                  foundDropTarget = entityId;
                  break;
                }
              }
            }
          }

          setValidDropTarget(foundDropTarget);
        }
      }
    },
    [
      dragState.isDragging,
      dragState.source,
      updateDragPosition,
      setValidDropTarget,
      state.sortedFinchEntities,
      state.sortedFilevineEntities,
    ],
  );

  // Handle mouse up to end drag
  const handleMouseUp = useCallback(() => {
    if (dragState.isDragging) {
      handleDragEnd();
    }
  }, [dragState.isDragging, handleDragEnd]);

  // Handle escape to cancel drag
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && dragState.isDragging) {
        cancelDrag();
      }
    },
    [dragState.isDragging, cancelDrag],
  );

  // Build connection lines from resolved links
  const connectionLines: ConnectionLineData[] = [];

  // Add approved link connections
  for (const link of state.approvedLinks) {
    const startPoint = getConnectionPoint(link.finchEntity.id);
    const endPoint = getConnectionPoint(link.filevineEntity.id);
    const line = calculateConnectionLine(
      startPoint,
      endPoint,
      link.id,
      'approved',
      link.confidence,
    );
    if (line) connectionLines.push(line);
  }

  // Add suggested link connections
  for (const link of state.suggestedLinks) {
    const startPoint = getConnectionPoint(link.finchEntity.id);
    const endPoint = getConnectionPoint(link.filevineEntity.id);
    const line = calculateConnectionLine(
      startPoint,
      endPoint,
      link.id,
      'suggested',
      link.confidence,
    );
    if (line) connectionLines.push(line);
  }

  // Check if there are entities that could potentially be linked
  const hasEntities =
    state.sortedFinchEntities.length > 0 ||
    state.sortedFilevineEntities.length > 0;
  const hasUnlinkedEntities =
    state.counts.unlinkedFinch > 0 || state.counts.unlinkedFilevine > 0;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={containerRef}
      className={cn('relative space-y-4', className)}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onKeyDown={handleKeyDown}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
    >
      {/* Header with stats and actions */}
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold text-zinc-900">
              Entity Linking
            </h3>

            {/* Stats badges */}
            <div className="flex items-center gap-2 text-sm">
              {state.counts.approved > 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-emerald-700"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>{state.counts.approved} linked</span>
                </motion.div>
              )}

              {state.counts.suggested > 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-amber-700"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>{state.counts.suggested} suggested</span>
                </motion.div>
              )}

              {(state.counts.unlinkedFinch > 0 ||
                state.counts.unlinkedFilevine > 0) && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-1 rounded-full bg-zinc-100 px-2.5 py-0.5 text-zinc-600"
                >
                  <CircleDashed className="h-3.5 w-3.5" />
                  <span>
                    {state.counts.unlinkedFinch + state.counts.unlinkedFilevine}{' '}
                    unlinked
                  </span>
                </motion.div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          {/* <div className="flex items-center gap-2">
          <Button
            onClick={onGenerateSuggestions}
            disabled={isGeneratingSuggestions || !hasUnlinkedEntities}
            size="sm"
            variant="outline"
          >
            {isGeneratingSuggestions ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                AI Link Suggestions
              </>
            )}
          </Button>

          {onGenerateSyncSuggestions && (
            <Button
              onClick={onGenerateSyncSuggestions}
              disabled={isGeneratingSyncSuggestions || !hasEntities}
              size="sm"
              variant="outline"
            >
              {isGeneratingSyncSuggestions ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <ArrowLeftRight className="mr-2 h-4 w-4" />
                  AI Sync Suggestions
                </>
              )}
            </Button>
          )}
        </div> */}
        </div>
      )}

      {/* Main two-column layout */}
      <div className="relative" data-linking-container>
        <div className="grid grid-cols-2 gap-24">
          {/* Finch column (left) */}
          <LinkingColumn
            title={canonicalSectionType === 'coverage' ? 'Finch Coverage' : 'Finch Providers'}
            icon={<Users className="h-4 w-4" />}
            entities={state.sortedFinchEntities}
            columnType="finch"
            canonicalSectionType={canonicalSectionType}
            fieldConfigs={fieldConfigs}
            sectionConfigData={sectionConfigData}
            matterId={matterId}
            onConnectionDotRef={registerConnectionDot}
            dragState={dragState}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            getLinkForEntity={getLinkForEntity}
            setValidDropTarget={setValidDropTarget}
            syncSuggestionsMap={syncSuggestionsMap}
            onSyncSuggestionClick={handleSyncSuggestionClick}
          />

          {/* Filevine column (right) */}
          <LinkingColumn
            title="Filevine Items"
            icon={<Users className="h-4 w-4" />}
            entities={state.sortedFilevineEntities}
            columnType="filevine"
            canonicalSectionType={canonicalSectionType}
            fieldConfigs={fieldConfigs}
            sectionConfigData={sectionConfigData}
            matterId={matterId}
            onConnectionDotRef={registerConnectionDot}
            dragState={dragState}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            getLinkForEntity={getLinkForEntity}
            setValidDropTarget={setValidDropTarget}
            syncSuggestionsMap={syncSuggestionsMap}
            onSyncSuggestionClick={handleSyncSuggestionClick}
          />
        </div>

        {/* SVG overlay for connection lines - positioned absolutely over the grid */}
        <ConnectionCanvas
          connections={connectionLines}
          dragState={dragState}
          onApprove={onApprove}
          onReject={onReject}
          onUnlink={onReject}
          suggestedLinks={state.suggestedLinks}
          approvedLinks={state.approvedLinks}
          getDropTargetPosition={(entityId) => getConnectionPoint(entityId)}
          syncSuggestionsMap={syncSuggestionsMap}
          onSyncSuggestionClick={handleSyncSuggestionClick}
        />
      </div>

      {/* Empty state - only show when both columns are empty */}
      <AnimatePresence>
        {state.sortedFinchEntities.length === 0 &&
          state.sortedFilevineEntities.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center rounded-lg border border-zinc-200 bg-zinc-50 py-12"
            >
              <Users className="h-10 w-10 text-zinc-300" />
              <p className="mt-3 text-sm text-zinc-500">
                No entities available for linking
              </p>
            </motion.div>
          )}
      </AnimatePresence>

      {/* Sync suggestion details sheet */}
      {selectedSyncSuggestions && selectedSyncSuggestions.length > 0 && (
        <SuggestionSetDetailsSheet
          suggestionSet={selectedSyncSuggestions[0]}
          open={isSyncSheetOpen}
          onOpenChange={handleSyncSheetOpenChange}
          finchEntity={selectedSyncEntities.finchEntity}
          filevineEntity={selectedSyncEntities.filevineEntity}
          fieldConfigs={fieldConfigs}
          firmId={firmId}
        />
      )}
    </div>
  );
};

export default UnifiedLinkingView;
